import { useCallback, useEffect, useRef, useState } from "react";
import { RotateCcw, MousePointer2 } from "lucide-react";

/**
 * Interactive hoodie designer.
 * Renders a clean hoodie panel in the buyer's chosen colour and lets them
 * DRAG the selected print design anywhere on it + RESIZE via a slider.
 * The current x/y (%) and size (%) are lifted to the parent so the exact
 * placement flows into the cart variant + the print-shop order.
 */
export const HoodieDesigner = ({
  hoodieColour,
  logoColour,
  printPreview,
  designLabel,
  recolourable,
  customText,
  customFontFamily,
  view, // "front" | "back"
  onViewChange,
  placement, // { x, y, size }  0-100 percentages
  onPlacementChange,
}) => {
  const panelRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ dx: 0, dy: 0 });

  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

  const startDrag = useCallback(
    (e) => {
      const target = e.currentTarget;
      const rect = target.getBoundingClientRect();
      // offset from top-left of the logo box, in %
      const pt = e.touches ? e.touches[0] : e;
      const dx = ((pt.clientX - rect.left) / rect.width) * placement.size;
      const dy = ((pt.clientY - rect.top) / rect.height) * placement.size;
      setDragOffset({ dx, dy });
      setDragging(true);
      e.preventDefault?.();
    },
    [placement.size]
  );

  const onMove = useCallback(
    (e) => {
      if (!dragging || !panelRef.current) return;
      // Prevent page scroll while dragging on touch devices
      if (e.cancelable) e.preventDefault?.();
      const rect = panelRef.current.getBoundingClientRect();
      const pt = e.touches ? e.touches[0] : e;
      const relX = ((pt.clientX - rect.left) / rect.width) * 100;
      const relY = ((pt.clientY - rect.top) / rect.height) * 100;
      const x = clamp(relX - dragOffset.dx, 0, 100 - placement.size);
      const y = clamp(relY - dragOffset.dy, 0, 100 - placement.size);
      onPlacementChange({ ...placement, x, y });
    },
    [dragging, dragOffset, placement, onPlacementChange]
  );

  const endDrag = useCallback(() => setDragging(false), []);

  useEffect(() => {
    if (!dragging) return;
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", endDrag);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", endDrag);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", endDrag);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", endDrag);
    };
  }, [dragging, onMove, endDrag]);

  const reset = () =>
    onPlacementChange({ x: 30, y: 30, size: view === "back" ? 55 : 25 });

  return (
    <div data-testid="hoodie-designer" className="w-full">
      {/* Front/Back toggle */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <button
          data-testid="designer-view-front"
          onClick={() => onViewChange("front")}
          className={`py-2 text-xs uppercase tracking-widest border transition-colors ${
            view === "front"
              ? "border-[#FF4500] text-[#FF4500] bg-[#FF4500]/5"
              : "border-white/20 text-white/70 hover:border-white"
          }`}
        >
          Front
        </button>
        <button
          data-testid="designer-view-back"
          onClick={() => onViewChange("back")}
          className={`py-2 text-xs uppercase tracking-widest border transition-colors ${
            view === "back"
              ? "border-[#FF4500] text-[#FF4500] bg-[#FF4500]/5"
              : "border-white/20 text-white/70 hover:border-white"
          }`}
        >
          Back
        </button>
      </div>

      {/* Hoodie panel — drag surface */}
      <div
        ref={panelRef}
        data-testid="hoodie-designer-panel"
        className="relative w-full aspect-[4/5] overflow-hidden border border-white/10 select-none touch-none"
        style={{ background: "radial-gradient(circle at 50% 30%, #1a1a1a 0%, #0A0A0A 70%)" }}
      >
        {/* Polished SVG hoodie illustration */}
        <svg
          viewBox="0 0 400 500"
          className="absolute inset-0 w-full h-full"
          preserveAspectRatio="xMidYMid meet"
          aria-hidden="true"
        >
          <defs>
            {/* body highlight — top-lit, subtle 3D */}
            <radialGradient id="bodyHighlight" cx="50%" cy="25%" r="70%">
              <stop offset="0%" stopColor="rgba(255,255,255,0.18)" />
              <stop offset="45%" stopColor="rgba(255,255,255,0)" />
              <stop offset="100%" stopColor="rgba(0,0,0,0.28)" />
            </radialGradient>
            {/* sleeve shading */}
            <linearGradient id="sleeveShadeL" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="rgba(0,0,0,0.35)" />
              <stop offset="60%" stopColor="rgba(255,255,255,0)" />
              <stop offset="100%" stopColor="rgba(0,0,0,0.25)" />
            </linearGradient>
            <linearGradient id="sleeveShadeR" x1="1" y1="0" x2="0" y2="0">
              <stop offset="0%" stopColor="rgba(0,0,0,0.35)" />
              <stop offset="60%" stopColor="rgba(255,255,255,0)" />
              <stop offset="100%" stopColor="rgba(0,0,0,0.25)" />
            </linearGradient>
            {/* hood inner shadow */}
            <radialGradient id="hoodShade" cx="50%" cy="80%" r="60%">
              <stop offset="0%" stopColor="rgba(0,0,0,0.55)" />
              <stop offset="100%" stopColor="rgba(0,0,0,0)" />
            </radialGradient>
            {/* soft ground shadow */}
            <radialGradient id="groundShadow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="rgba(0,0,0,0.5)" />
              <stop offset="100%" stopColor="rgba(0,0,0,0)" />
            </radialGradient>
            {/* subtle fabric texture noise */}
            <filter id="fabric" x="0" y="0" width="100%" height="100%">
              <feTurbulence type="fractalNoise" baseFrequency="1.4" numOctaves="1" seed="7" />
              <feColorMatrix values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.08 0" />
              <feComposite in2="SourceGraphic" operator="in" />
            </filter>
          </defs>

          {/* ground shadow */}
          <ellipse cx="200" cy="475" rx="130" ry="10" fill="url(#groundShadow)" />

          {/* LEFT SLEEVE */}
          <path
            d="M118,178 C90,190 55,220 40,260 C35,280 32,310 40,340 L92,352 C98,320 108,285 118,255 Z"
            fill={hoodieColour}
            stroke="rgba(0,0,0,0.35)"
            strokeWidth="1"
            strokeLinejoin="round"
          />
          <path
            d="M118,178 C90,190 55,220 40,260 C35,280 32,310 40,340 L92,352 C98,320 108,285 118,255 Z"
            fill="url(#sleeveShadeL)"
          />
          {/* left cuff ribbing */}
          <path
            d="M40,340 L92,352 L88,368 L44,356 Z"
            fill={hoodieColour}
            stroke="rgba(0,0,0,0.35)"
            strokeWidth="1"
          />
          <line x1="46" y1="345" x2="90" y2="357" stroke="rgba(0,0,0,0.25)" strokeWidth="0.6" />
          <line x1="45" y1="350" x2="89" y2="362" stroke="rgba(0,0,0,0.25)" strokeWidth="0.6" />

          {/* RIGHT SLEEVE */}
          <path
            d="M282,178 C310,190 345,220 360,260 C365,280 368,310 360,340 L308,352 C302,320 292,285 282,255 Z"
            fill={hoodieColour}
            stroke="rgba(0,0,0,0.35)"
            strokeWidth="1"
            strokeLinejoin="round"
          />
          <path
            d="M282,178 C310,190 345,220 360,260 C365,280 368,310 360,340 L308,352 C302,320 292,285 282,255 Z"
            fill="url(#sleeveShadeR)"
          />
          {/* right cuff ribbing */}
          <path
            d="M360,340 L308,352 L312,368 L356,356 Z"
            fill={hoodieColour}
            stroke="rgba(0,0,0,0.35)"
            strokeWidth="1"
          />
          <line x1="354" y1="345" x2="310" y2="357" stroke="rgba(0,0,0,0.25)" strokeWidth="0.6" />
          <line x1="355" y1="350" x2="311" y2="362" stroke="rgba(0,0,0,0.25)" strokeWidth="0.6" />

          {/* BODY PANEL */}
          <path
            d="M118,175
               C118,155 148,140 175,138
               L225,138
               C252,140 282,155 282,175
               L288,430
               C288,445 275,455 258,456
               L142,456
               C125,455 112,445 112,430 Z"
            fill={hoodieColour}
            stroke="rgba(0,0,0,0.4)"
            strokeWidth="1.2"
            strokeLinejoin="round"
          />
          {/* body highlight */}
          <path
            d="M118,175 C118,155 148,140 175,138 L225,138 C252,140 282,155 282,175 L288,430 C288,445 275,455 258,456 L142,456 C125,455 112,445 112,430 Z"
            fill="url(#bodyHighlight)"
          />
          {/* fabric texture */}
          <path
            d="M118,175 C118,155 148,140 175,138 L225,138 C252,140 282,155 282,175 L288,430 C288,445 275,455 258,456 L142,456 C125,455 112,445 112,430 Z"
            filter="url(#fabric)"
          />

          {/* HEM RIBBING */}
          <rect x="112" y="446" width="176" height="12" fill={hoodieColour} stroke="rgba(0,0,0,0.35)" strokeWidth="1" />
          <line x1="115" y1="450" x2="285" y2="450" stroke="rgba(0,0,0,0.2)" strokeWidth="0.6" />
          <line x1="115" y1="455" x2="285" y2="455" stroke="rgba(0,0,0,0.2)" strokeWidth="0.6" />

          {/* HOOD */}
          {view === "front" ? (
            <>
              {/* left hood flap */}
              <path
                d="M155,140 C158,80 178,55 200,55 C222,55 242,80 245,140 L235,148 C230,110 218,95 200,95 C182,95 170,110 165,148 Z"
                fill={hoodieColour}
                stroke="rgba(0,0,0,0.4)"
                strokeWidth="1.2"
                strokeLinejoin="round"
              />
              {/* hood inner shadow (V-neck opening) */}
              <path
                d="M165,148 C170,110 182,95 200,95 C218,95 230,110 235,148 L215,148 L200,195 L185,148 Z"
                fill="url(#hoodShade)"
              />
              <path d="M185,148 L200,195 L215,148 Z" fill="rgba(0,0,0,0.65)" />
              {/* Drawstrings */}
              <path d="M193,152 C193,180 195,210 197,225" fill="none" stroke="rgba(0,0,0,0.55)" strokeWidth="1.8" strokeLinecap="round" />
              <path d="M207,152 C207,180 205,210 203,225" fill="none" stroke="rgba(0,0,0,0.55)" strokeWidth="1.8" strokeLinecap="round" />
              <circle cx="197" cy="227" r="3.5" fill="rgba(0,0,0,0.65)" />
              <circle cx="203" cy="227" r="3.5" fill="rgba(0,0,0,0.65)" />
              {/* Kangaroo pocket */}
              <path
                d="M135,325 L265,325 L282,405 L118,405 Z"
                fill="none"
                stroke="rgba(0,0,0,0.35)"
                strokeWidth="1.2"
                strokeLinejoin="round"
              />
              {/* pocket depth */}
              <line x1="135" y1="325" x2="149" y2="358" stroke="rgba(0,0,0,0.3)" strokeWidth="0.8" />
              <line x1="265" y1="325" x2="251" y2="358" stroke="rgba(0,0,0,0.3)" strokeWidth="0.8" />
              {/* pocket opening slits */}
              <line x1="149" y1="358" x2="149" y2="378" stroke="rgba(0,0,0,0.4)" strokeWidth="1.2" strokeLinecap="round" />
              <line x1="251" y1="358" x2="251" y2="378" stroke="rgba(0,0,0,0.4)" strokeWidth="1.2" strokeLinecap="round" />
            </>
          ) : (
            <>
              {/* back hood (flat, laid down) */}
              <path
                d="M148,140 C155,75 178,58 200,58 C222,58 245,75 252,140 Z"
                fill={hoodieColour}
                stroke="rgba(0,0,0,0.4)"
                strokeWidth="1.2"
                strokeLinejoin="round"
              />
              {/* hood shading */}
              <path
                d="M148,140 C155,75 178,58 200,58 C222,58 245,75 252,140 Z"
                fill="url(#hoodShade)"
                opacity="0.6"
              />
              {/* hood centre crease */}
              <path d="M200,58 L200,138" stroke="rgba(0,0,0,0.3)" strokeWidth="0.8" strokeDasharray="2,3" />
              {/* Back neck seam */}
              <path d="M170,146 C185,138 215,138 230,146" fill="none" stroke="rgba(0,0,0,0.5)" strokeWidth="1.5" strokeLinecap="round" />
              {/* subtle back centre seam */}
              <path d="M200,150 L200,440" stroke="rgba(0,0,0,0.08)" strokeWidth="0.8" strokeDasharray="1,3" />
            </>
          )}

          {/* SHOULDER SEAMS */}
          <path d="M118,178 C130,168 145,158 165,152" fill="none" stroke="rgba(0,0,0,0.28)" strokeWidth="0.9" />
          <path d="M282,178 C270,168 255,158 235,152" fill="none" stroke="rgba(0,0,0,0.28)" strokeWidth="0.9" />
        </svg>

        {/* Print overlay — draggable */}
        {(printPreview || customText) && (
          <div
            data-testid="hoodie-designer-print"
            onPointerDown={startDrag}
            onTouchStart={startDrag}
            className={`absolute cursor-grab active:cursor-grabbing transition-shadow ${
              dragging ? "ring-2 ring-[#FF4500] ring-offset-2 ring-offset-transparent shadow-2xl" : "hover:ring-1 hover:ring-white/40"
            }`}
            style={{
              top: `${placement.y}%`,
              left: `${placement.x}%`,
              width: `${placement.size}%`,
              aspectRatio: "1 / 1",
              touchAction: "none",
            }}
            aria-label={`Drag ${designLabel} — currently at ${Math.round(placement.x)}% ${Math.round(placement.y)}%`}
          >
            {customText ? (
              /* Custom name — rendered as SVG text so it scales with the box
                 and can be tinted with any font colour. `textLength` +
                 `lengthAdjust=spacingAndGlyphs` keeps long names inside the
                 draggable box. */
              <svg
                viewBox="0 0 200 100"
                preserveAspectRatio="xMidYMid meet"
                className="w-full h-full pointer-events-none"
                style={{ filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.35))" }}
              >
                <text
                  x="100"
                  y="60"
                  textAnchor="middle"
                  fill={logoColour}
                  textLength={customText.length > 8 ? 180 : undefined}
                  lengthAdjust="spacingAndGlyphs"
                  style={{
                    fontFamily: customFontFamily || "'Anton', sans-serif",
                    fontSize: customText.length > 12 ? 34 : 44,
                    letterSpacing: "1px",
                  }}
                >
                  {customText}
                </text>
              </svg>
            ) : recolourable ? (
              /* Single-colour designs (Cam's Koori Art brush, OCK mono).
                 Source PNGs are pre-processed to be transparent-bg + solid-black ink.
                 CSS mask uses the alpha channel by default, and background-color
                 fills the ink pixels with the chosen logo colour. */
              <div
                className="w-full h-full"
                style={{
                  backgroundColor: logoColour,
                  WebkitMaskImage: `url("${printPreview}")`,
                  maskImage: `url("${printPreview}")`,
                  WebkitMaskRepeat: "no-repeat",
                  maskRepeat: "no-repeat",
                  WebkitMaskSize: "contain",
                  maskSize: "contain",
                  WebkitMaskPosition: "center",
                  maskPosition: "center",
                  filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.35))",
                  pointerEvents: "none",
                }}
              />
            ) : (
              /* Multi-colour emblems — pre-processed to have transparent
                 backgrounds so the hoodie fabric shows through around them,
                 preserving the emblem's baked-in colours as-is. */
              <img
                src={printPreview}
                alt={designLabel}
                draggable={false}
                className="w-full h-full object-contain pointer-events-none"
                style={{
                  filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.35))",
                }}
              />
            )}
          </div>
        )}

        {/* Hint overlay (visible until first drag) */}
        {!dragging && (
          <div className="absolute bottom-3 left-3 right-3 z-10 flex items-center justify-center gap-2 text-[10px] uppercase tracking-widest text-white/60 bg-[#0A0A0A]/70 backdrop-blur border border-white/10 px-3 py-1.5 pointer-events-none">
            <MousePointer2 className="w-3 h-3" /> Drag the print to position · slide to resize
          </div>
        )}

        {/* Corner mark showing current view */}
        <div className="absolute top-3 right-3 text-[10px] uppercase tracking-widest text-white/70 bg-[#0A0A0A]/70 backdrop-blur border border-white/10 px-2 py-1">
          {view === "front" ? "Front view" : "Back view"}
        </div>
      </div>

      {/* Size slider + reset + numeric readout */}
      <div className="mt-4 space-y-3">
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="overline">Print size</label>
            <span data-testid="designer-size-value" className="text-xs text-[#FF4500] font-display tracking-wider">
              {Math.round(placement.size)}%
            </span>
          </div>
          <input
            data-testid="designer-size-slider"
            type="range"
            min={10}
            max={90}
            step={1}
            value={placement.size}
            onChange={(e) => {
              const next = Number(e.target.value);
              const maxX = 100 - next;
              const maxY = 100 - next;
              onPlacementChange({
                size: next,
                x: clamp(placement.x, 0, maxX),
                y: clamp(placement.y, 0, maxY),
              });
            }}
            className="w-full accent-[#FF4500]"
          />
        </div>
        <div className="flex items-center justify-between gap-3 text-[11px] uppercase tracking-widest">
          <div data-testid="designer-position-readout" className="text-white/50">
            Position <span className="text-white/80">{Math.round(placement.x)}% × {Math.round(placement.y)}%</span>
          </div>
          <button
            data-testid="designer-reset"
            onClick={reset}
            className="inline-flex items-center gap-1.5 text-white/70 hover:text-[#FF4500] transition-colors"
          >
            <RotateCcw className="w-3 h-3" /> Reset
          </button>
        </div>
      </div>
    </div>
  );
};

export default HoodieDesigner;
